/**
 * Central route registry.
 *
 * Every internal URL is built from here so a path change is a one-line edit and
 * so extranet sections are real, shareable, bookmarkable URLs instead of local
 * component state.
 */

export type ExtranetRole = "hotel" | "saas" | "internal";

/* -------------------------------------------------------------------------- */
/* Sections                                                                    */
/* -------------------------------------------------------------------------- */

export const HOTEL_SECTIONS = [
  "details",
  "credentials",
  "integrations",
  "logs",
  "notifications",
  "settings",
] as const;

export const SAAS_SECTIONS = [
  "details",
  "hotels",
  "pms_info",
  "scope",
  "webhooks",
  "onboarding",
  "widgetHotels",
  "logs",
  "apiToken",
  "notifications",
  "settings",
] as const;

export const INTERNAL_SECTIONS = [
  "pms",
  "hotels",
  "saas",
  "logs",
  "queues",
  "warehouse",
  "warehouse_analytics",
  "warehouse_crm",
  "settings",
] as const;

export type HotelSection = (typeof HOTEL_SECTIONS)[number];
export type SaasSection = (typeof SAAS_SECTIONS)[number];
export type InternalSection = (typeof INTERNAL_SECTIONS)[number];
export type ExtranetSection = HotelSection | SaasSection | InternalSection;

export const EXTRANET_SECTIONS = {
  hotel: HOTEL_SECTIONS,
  saas: SAAS_SECTIONS,
  internal: INTERNAL_SECTIONS,
} as const satisfies Record<ExtranetRole, readonly string[]>;

/** Section landed on when the URL carries none (or an unknown one). */
export const DEFAULT_SECTION = {
  hotel: "details",
  saas: "details",
  internal: "pms",
} as const satisfies Record<ExtranetRole, ExtranetSection>;

/** Sub-sections (second path segment) of the sections that have tabs. */
export const INTERNAL_PMS_TABS = ["list", "scopes"] as const;
export type InternalPmsTab = (typeof INTERNAL_PMS_TABS)[number];

/* -------------------------------------------------------------------------- */
/* Path builders                                                               */
/* -------------------------------------------------------------------------- */

type Query = Record<string, string | number | null | undefined>;

function withQuery(path: string, query?: Query): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== null && value !== undefined && value !== "") {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/** `/extranet/:role[/:section[/:sub]]` — the canonical extranet URL. */
export function extranetPath(
  role: ExtranetRole,
  section?: string,
  sub?: string,
  query?: Query,
): string {
  const segments = ["/extranet", role];
  if (section) segments.push(section);
  if (section && sub) segments.push(sub);
  return withQuery(segments.join("/"), query);
}

export const ROUTES = {
  home: "/",
  login: "/login",
  signup: "/signup",
  forgotPassword: "/forgot-password",
  resetPassword: "/reset-password",
  settings: "/settings",
  apiDocumentation: "/api-documentation",
  hotelOnboarding: "/onboarding/hotel",
  saasOnboarding: "/onboarding/saas",
  onboardingWidget: (saasId: string, query?: Query) =>
    withQuery(`/onboarding/widget/${encodeURIComponent(saasId)}`, query),
  extranet: (role: ExtranetRole, section?: string, sub?: string, query?: Query) =>
    extranetPath(role, section, sub, query),
  hotelExtranet: (section?: HotelSection, query?: Query) =>
    extranetPath("hotel", section, undefined, query),
  saasExtranet: (section?: SaasSection, query?: Query) =>
    extranetPath("saas", section, undefined, query),
  internalExtranet: (section?: InternalSection, sub?: string, query?: Query) =>
    extranetPath("internal", section, sub, query),
} as const;

/** Route patterns registered in App.tsx (kept next to the builders above). */
export const ROUTE_PATTERNS = {
  extranet: "/extranet/:role",
  extranetSection: "/extranet/:role/:section",
  extranetSubSection: "/extranet/:role/:section/:sub",
} as const;

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

export function isExtranetRole(value: unknown): value is ExtranetRole {
  return value === "hotel" || value === "saas" || value === "internal";
}

/** Narrows a raw URL segment to a known section, else `null`. */
export function parseSection<T extends readonly string[]>(
  sections: T,
  value: string | undefined,
): T[number] | null {
  return value && (sections as readonly string[]).includes(value)
    ? (value as T[number])
    : null;
}
